

import React, { useState, useEffect } from "react";
import { Modal, Select, Checkbox, Spin, Typography, Input, Alert } from "antd";
import "./index.scss";
import { DeleteOutlined } from "@ant-design/icons";
  
const { Option } = Select;

// ---------------------------------------------------------------------------
// Shared reason options (used by both regular delete and delete-marker flows)
// ---------------------------------------------------------------------------
export const DELETE_REASONS = [
  { value: "Operational-Correction-of-data-by-vendor", label: "Operational - Correction of data by vendor" },
  { value: "Operational-Correction-incomplete-or-corrupt-upload", label: "Operational - Correction - incomplete or corrupt upload" },
  { value: "Operational-Correction-file-uploaded-to-wrong-location", label: "Operational - Correction - file uploaded to wrong location" },
  { value: "Operational-Other", label: "Operational - Other" },
  { value: "Compliance-withdrawal-of-patient-consent", label: "Compliance - Withdrawal of patient consent" },
  { value: "Compliance-loss-off-rights-to-data", label: "Compliance - Loss of rights to data" },
  { value: "Compliance-life-cycle-expiration", label: "Compliance - Life cycle expiration" },
  { value: "Compliance-Other", label: "Compliance - Other" },
];

// ---------------------------------------------------------------------------
// DeleteMarkerModal — GxP approval-gated delete marker removal
// ---------------------------------------------------------------------------

export interface DeleteMarkerModalProps {
  visible: boolean;
  /** The file key the delete marker belongs to, shown in the modal for context */
  fileKey: string;
  onOk: (reason: string) => void;
  onCancel: () => void;
  loading: boolean;
}

export const DeleteMarkerModal: React.FC<DeleteMarkerModalProps> = ({
  visible,
  fileKey,
  onOk,
  onCancel,
  loading,
}) => {
  const [reason, setReason] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!visible) {
      setReason("");
      setError("");
    }
  }, [visible]);

  const handleOk = () => {
    if (!reason) {
      setError("Please select a reason for the delete marker removal.");
      return;
    }
    setError("");
    onOk(reason);
  };

  const handleCancel = () => {
    setReason("");
    setError("");
    onCancel();
  };

  return (
    <Modal
      title={
        <span>
          <DeleteOutlined style={{ color: "#cf1322", marginRight: 8 }} />
          Remove Delete Marker
        </span>
      }
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Submit for Approval"
      okButtonProps={{ danger: true }}
      confirmLoading={loading}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="GxP Approval Required"
        description={
          <>
            Removing a delete marker will restore this file's visibility in the GxP space.
            This action requires approval from another space owner before it takes effect.
            <br /><br />
            <strong>File:</strong> {fileKey}
          </>
        }
      />

      <Typography.Title level={5} style={{ marginBottom: 4, color: "#3c3c3c", fontWeight: 600 }}>
        Reason for Delete Marker Removal:
      </Typography.Title>
      <Select
        style={{ width: "100%", marginTop: 4 }}
        value={reason || undefined}
        onChange={setReason}
        placeholder="Please select a reason"
      >
        {DELETE_REASONS.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}

      {loading && <Spin style={{ marginTop: 10 }} />}
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// DeleteFileModal — existing regular file delete modal (unchanged behaviour)
// ---------------------------------------------------------------------------

export interface DeleteFileModalProps {
  visible: boolean;
  onOk: (reason: string, permanent: boolean) => void;
  onCancel: () => void;
  loading: boolean;
  fileCount: number;
  spaceType?: string; // GxP flag to determine if space is Non-GxP
}

const DELETE_REASONS_INTERNAL = DELETE_REASONS;

const DeleteFileModal: React.FC<DeleteFileModalProps> = ({
  visible,
  onOk,
  onCancel,
  loading,
  fileCount,
  spaceType,
}) => {
  const [reason, setReason] = useState<string>("");
  const [permanentDelete, setPermanentDelete] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");

  // Check if this is a Non-GxP space
  const isNonGxpSpace = spaceType?.toLowerCase() === "nongxp" || spaceType?.toLowerCase() === "non-gxp";
  const isOtherReason = reason.endsWith("-Other");

  useEffect(() => {
    if (!visible) {
      setReason("");
      setPermanentDelete(false);
      setError("");
      setOtherReason("");
    }
  }, [visible]);
  const handleOk = () => {
    // For Non-GxP spaces, we don't require a deletion reason
    if (isNonGxpSpace || reason) {
      if (!isNonGxpSpace && isOtherReason && otherReason.trim().length < 10) {
        setError("Please provide at least 10 characters for the 'Other' reason.");
        return;
      }
      setError("");
      // For Non-GxP spaces, use default values that won't be sent to API
      const reasonToUse = isNonGxpSpace
        ? "not-applicable-for-non-gxp"
        : isOtherReason
          ? otherReason.trim()
          : reason;
      const permanentDeleteToUse = isNonGxpSpace ? false : permanentDelete;
      onOk(reasonToUse, permanentDeleteToUse);
    } else {
      setError("Please select a reason for deletion.");
    }
  };

  const handleCancel = () => {
    setReason("");
    setPermanentDelete(false);
    setError("");
    setOtherReason("");
    onCancel();
  };

  return (
    <Modal
      title={"Delete Files/Folders"}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Delete"
      confirmLoading={loading}
      destroyOnClose
      okButtonProps={{
        disabled: !isNonGxpSpace && (!reason || (isOtherReason && otherReason.trim().length < 10)),
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Typography.Title level={5} style={{ marginBottom: 0, color: "#3c3c3c", fontWeight: 600 }}>
            {isNonGxpSpace
              ? "Do you want to proceed with deletion?"
              : "Select Reason for Deletion:"}
        </Typography.Title>
        {/* Only show reason selection for GxP spaces */}
        {!isNonGxpSpace && (
          <>
            <Select
              style={{ width: "100%", marginTop: 6 }}
              value={reason}
              onChange={(val) => { setReason(val); setOtherReason(""); setError(""); }}
              placeholder="Please select a reason for deletion"
            >
              {DELETE_REASONS_INTERNAL.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            {isOtherReason && (
              <>
                <Input
                  style={{ marginTop: 8 }}
                  placeholder="Please describe the reason (minimum 10 characters)"
                  value={otherReason}
                  onChange={(e) => { setOtherReason(e.target.value); setError(""); }}
                  maxLength={500}
                />
              </>
            )}
            {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
          </>
        )}
      </div>
      {/* Only show permanent delete checkbox for GxP spaces */}
      {!isNonGxpSpace && (
        <>
          <Checkbox
            checked={permanentDelete}
            onChange={e => setPermanentDelete(e.target.checked)}
          >
            Permanent Delete
          </Checkbox>
          <div style={{ color: "red", marginTop: 10, fontSize: 12 }}>
            Warning: Objects deleted permanently will not be recoverable.
          </div>
        </>
      )}
      {loading && <Spin style={{ marginTop: 10 }} />}
    </Modal>
  );
};

export default DeleteFileModal;
